import { MutableRefObject, useEffect, useRef, useState } from "react";
import { TransformTranslate } from "../../Util/Transform/Transform";
import { HxMapGroup } from "../HxMap/HxMapGroup";
import { HexMapBuilder, HexMapBuilderParams } from "../../Data/HexMap/HexMapBuilder";
import { Music } from "../../Util/Music/Music";
import { HexMap } from "../../Data/HexMap/HexMap";
import { HexMapModifier } from "../../Data/HexMap/HexMapModifier";
import { produce } from "immer";
import { All } from "../../Util/Constants/Quantities";
import { Note } from "../../Data/HexMap/Note";
import { Key } from "../../Data/HexMap/Key";
import { NotesSelectMenu } from "../NoteButtons/NotesSelect";
import './FreeSpace.scss'
import { RowsAndColumns } from "./RowsAndColumns";
import Draggable from "react-draggable";
import { NotePlayer } from "../../Sound/NotePlayer";
import { playChord } from "../../Data/HexMap/NoteUtils";
import { Chords } from "../../Util/Music/Chords";
import { Menu, MenuItem, MenuRadioGroup, RadioChangeEvent } from "@szhsin/react-menu";
import { MenuArrow } from "../Menu/MenuDownArrow";
import { useNoteCtrlMenuStore } from "../CtrlMenus/NoteCtrlMenuStore";
import { NoteCtrlMenuState } from "../CtrlMenus/NoteCtrlMenuState";
import { Coordinates } from "../../Util/Math/Coordinates";
import { KeyCtrlMenuState } from "../CtrlMenus/KeyCtrlMenuState";
import { useKeyCtrlMenuStore } from "../CtrlMenus/KeyCtrlMenuStore";
import { useNotePlayer } from "../../Sound/SoundStore";

export function FreeSpace(props:{soundMode:boolean|null, width?:number, hiddenKeys?:number[], keysOpen?:boolean, tonic?:number, rows?:number, columns?:number, radius?:number, innerScale?:number}){
    const viewWidth:number = (props.width) ? props.width : 1000
    const viewHeight:number = 600
    const keyRadius = (props.radius) ? props.radius : 100
    const [soundMode, setSoundMode] = useState<boolean|null>(props.soundMode)
    const [selectMode, setSelectMode] = useState<boolean>(true)
    const [outerScale, setOuterScale] = useState(1)
    const [tonic, setTonic] = useState((props.tonic) ? props.tonic : Music.G)
    const rows:MutableRefObject<number> = useRef((props.rows)?props.rows:3)
    const columns:MutableRefObject<number> = useRef((props.columns)?props.columns:4)
    const keysOpen:MutableRefObject<boolean> = useRef((props.keysOpen === true))
    const notePlayer:MutableRefObject<NotePlayer> = useRef(useNotePlayer())
    useEffect(()=>{setSoundMode(props.soundMode)},[props.soundMode])
    useEffect(()=>{if(props.tonic !== undefined){
        onTonicSelect(props.tonic)
    }},[props.tonic])


    const initializeMap = (mod:HexMapModifier) => {
        mod.notes_setVisible([All], true)
        mod.keys_setOpen([All], keysOpen.current)
        if(props.hiddenKeys){
            mod.keys_setOpen(props.hiddenKeys, false)
            mod.keys_setVisible(props.hiddenKeys, false)
        }
        
    }
    const getHexMap = () => {
        const params:HexMapBuilderParams = {
            rows:rows.current, columns:columns.current, tonic:tonic, radius:keyRadius
        }
        const map:HexMap = new HexMapBuilder(params).build()
        const mod:HexMapModifier = new HexMapModifier(map)
        initializeMap(mod)
        return map
    }
    const [map, setMap] = useState(getHexMap())
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(map))
    const onKeyClick = (key:Key, e:React.MouseEvent, ctrl?:boolean) => {
        if(ctrl){
            onKeyCtrl(key, e)
            return
        }
        if(e.metaKey){
            setMap(produce((draft:HexMap)=>{ 
                    mod.current.setDraft(draft)
                    mod.current.keys_setOpen([key.params.keyIndex], !key.open)
                    mod.current.keys_removeClasses([key.params.keyIndex], ['ghosted'])
            }))
            return
        }
        if(e.shiftKey){
            setMap(produce((draft:HexMap)=>{ 
                    mod.current.setDraft(draft)
                    mod.current.keys_setOpen([key.params.keyIndex], false)
                    mod.current.keys_toggleClasses([key.params.keyIndex], ['ghosted'])
            }))  
            return
        }
        setMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_advanceSelectedState([key.params.keyIndex])
        }))   

    }
    const keyConfigSetter:Function = useKeyCtrlMenuStore((state:KeyCtrlMenuState)=>state.setKeyConfig)
    
    const onKeyCtrl = (key:Key, e:React.MouseEvent) => {
        keyConfigSetter(key, new Coordinates(e.clientX, e.clientY), setMap)
    }
    const ghostUnopenedKeys = () => {
        setMap(produce((draft:HexMap)=>{ 
            mod.current.setDraft(draft)
            draft.keys.flat().forEach((key:Key)=>{
                if(!key.open){
                    mod.current.keys_addClasses([key.params.keyIndex], ['ghosted'])
                }
            })
        }))   
    }
    const noteConfigSetter:Function = useNoteCtrlMenuStore((state:NoteCtrlMenuState)=>state.setNoteConfig)
    const onNoteCtrl = (note:Note, e:React.MouseEvent) => {
        noteConfigSetter(note, new Coordinates(e.clientX, e.clientY), setMap)
    }
    const onNoteClick = (note:Note, e:React.MouseEvent, ctrl?:boolean) => {
        if(ctrl){
            onNoteCtrl(note, e)
            return
        }
        if(soundMode!== null){
            if(soundMode === true){
                const formula:number[] = (e.metaKey) ? Chords.Formula_seventh : Chords.Formula_triad
                playChord(note, formula, e, map, notePlayer.current)

            }
            else{
                notePlayer.current.playNote(note.params.tonic, true)
            }
        }
        if(selectMode){
            setMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.notes_advanceSelectedState([note.params.noteIndex])
            }))
        }
    }
    const deselectAllNotes = () => {
        setMap(produce((draft:HexMap)=>{ 
            mod.current.setDraft(draft)
            mod.current.notes_setSelectedState([All], Note.SelectedState_Default)
        }))
    }
    const deselectAllKeys = () => {
        setMap(produce((draft:HexMap)=>{ 
            mod.current.setDraft(draft)
            mod.current.keys_setSelectedState([All], Note.SelectedState_Default)
        }))
    }
    const setAllKeysOpen = (open:boolean) => {
        keysOpen.current = open
        setMap(produce((draft:HexMap)=>{ 
            mod.current.setDraft(draft)
            mod.current.keys_setOpen([All], open)
            mod.current.keys_removeClasses([All], ['ghosted'])
        }))
    }
    const getInnerScale = () => {
        if(props.innerScale){
            return props.innerScale
        }
        const maxX:number = viewWidth - 200
        const maxY:number = viewHeight - 200
        const [x,y] = map.getFullDimensions()
        const yScale:number = (y > maxY) ? maxY/y : maxY/y
        const xScale:number = (x > maxX) ? maxX/x : maxX/x
        let scale:number = (yScale < xScale) ? yScale : xScale 
        scale = (scale > 2) ? 2 : scale
        return (scale)
    }
    const onTonicSelect = (freshTonic:number) => {
        setTonic(freshTonic)
        // change tonic of map
        setMap(produce((draft:HexMap)=>{ 
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.setTonic(freshTonic)
        }))
    }
    const onRowsColumnsUpdate = (freshRows:number, freshColumns:number) => {
        rows.current = freshRows
        columns.current = freshColumns
        setMap(getHexMap())
    }
    const showTwelveKeyCluster = () => {
        rows.current = 3
        columns.current = 5
        const freshMap:HexMap = getHexMap()
        const freshMod:HexMapModifier = new HexMapModifier(freshMap)
        freshMod.keys_setOpen([4,9,10],false)
        freshMod.keys_setVisible([4,9,10],false)
        setMap(freshMap)
    }
    const onZoomIn = () => {
        setOuterScale(outerScale * 1.25)
    }
    const onZoomOut = () => {
        setOuterScale(outerScale * .75)
    }
    const nodeRef = useRef(null)
    const getSoundModeLabel = () => {
        if(soundMode === null){return 'Off'}
        return (soundMode === true) ? 'Chords' : 'Notes'
    }
    return(
        <div className='free-space'>
            <div className={'controls'}>
                <RowsAndColumns rows={rows.current} columns={columns.current} onUpdate={onRowsColumnsUpdate}/>
                <button type='button' onClick={onZoomIn}>[ + ]</button>
                <button type='button' onClick={onZoomOut}>[ - ]</button>
                <NotesSelectMenu onChange={onTonicSelect} tonic={tonic}/>
                <Menu menuButton={  <button type='button'>
                                        Sound Mode: {getSoundModeLabel()} <MenuArrow/>
                                    </button>}>
                    <MenuRadioGroup value={soundMode} onRadioChange={(e:RadioChangeEvent)=>{setSoundMode(e.value)}}>
                            <MenuItem type="radio" value={null}>Off</MenuItem>
                            <MenuItem type="radio" value={true}>Chords</MenuItem>
                            <MenuItem type="radio" value={false}>Notes</MenuItem>
                    </MenuRadioGroup>
                </Menu>
                <Menu menuButton={  <button type='button'>
                                        Selection Mode: {(selectMode) ? 'On' : 'Off'} <MenuArrow/>
                                    </button>}>
                    <MenuRadioGroup value={selectMode} onRadioChange={(e:RadioChangeEvent)=>{setSelectMode(e.value)}}>
                            <MenuItem type="radio" value={true}>On</MenuItem>
                            <MenuItem type="radio" value={false}>Off</MenuItem>
                    </MenuRadioGroup>
                </Menu>
                <Menu menuButton={<button type='button'>Notes <MenuArrow/></button>}>
                    <MenuItem onClick={deselectAllNotes} value={false}>Deselect All</MenuItem>
                </Menu>
                <Menu menuButton={<button type='button'>Keys <MenuArrow/></button>}>
                    <MenuItem onClick={deselectAllKeys} value={false}>Deselect All</MenuItem>
                    <MenuItem onClick={()=>{setAllKeysOpen(true)}} value={false}>Open All</MenuItem>
                    <MenuItem onClick={()=>{setAllKeysOpen(false)}} value={false}>Close All</MenuItem>
                    <MenuItem onClick={ghostUnopenedKeys} value={false}>Ghost All Unopened</MenuItem>
                    <MenuItem onClick={showTwelveKeyCluster} value={false}>Show 12-Key Cluster</MenuItem>
                </Menu>
            </div>

        
            <svg width={viewWidth} height={viewHeight}>
                <Draggable nodeRef={nodeRef}>
                    <g ref={nodeRef}><g transform={TransformTranslate(viewWidth/2,viewHeight/2)}>
                        <g style={{scale:outerScale}}>
                            <g style={{scale:getInnerScale()}}>
                                <HxMapGroup ctrl={true} hexMap={map} onKeyClick={onKeyClick} onNoteClick={onNoteClick}/>
                            </g>
                        </g>
                    </g>
                </g>
                </Draggable>
            </svg>
        
        </div>
    )
}